import React from "react";
import DOMPurify from "dompurify";
import type { PreviewWine } from "./UpcomingCase";

const FACT = /region|grape|appellation|vintage|style|colour|country/i;

export function CaseShowcase({ wines, tone = "var(--club-dark)" }: { wines: PreviewWine[]; tone?: string }) {
  return (
    <div data-testid="case-showcase">
      <div className="mt-10 border border-border bg-white px-4 py-8 sm:px-8" data-testid="case-shelf">
        <div className="eyebrow text-center" style={{ color: tone }}>The twelve · side by side</div>
        <ul className="mt-6 flex snap-x gap-6 overflow-x-auto pb-4 sm:grid sm:grid-cols-6 sm:gap-4 sm:overflow-visible lg:gap-6">
          {wines.map((w, i) => (
            <li key={w.product_handle} className="flex w-32 shrink-0 snap-start flex-col items-center sm:w-auto rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`shelf-bottle-${w.product_handle}`}>
              <a href={`#wine-${i + 1}`} className="group block aspect-[2/5] w-full overflow-hidden">{w.image ? <img src={w.image} alt={w.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" /> : <span className="eyebrow">No photo</span>}</a>
              <div className="mt-1 h-px w-full bg-border" />
              <div className="mt-2 font-brand text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: tone }}>{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-1 text-center text-xs leading-snug text-neutral-700">{w.title}</div>
            </li>
          ))}
        </ul>
      </div>

      <ol className="mt-12 space-y-6" data-testid="case-features">
        {wines.map((w, i) => {
          const facts = w.facts.filter(([k]) => FACT.test(k)).slice(0, 4);
          const flip = i % 2 === 1;
          return (
            <li key={w.product_handle} id={`wine-${i + 1}`} className={`grid gap-8 border border-border bg-[var(--paper)] p-6 lg:grid-cols-12 lg:p-10 ${flip ? "" : ""}`} data-testid={`feature-wine-${w.product_handle}`}>
              <div className={`aspect-[3/4] overflow-hidden bg-white lg:col-span-4 lg:aspect-auto lg:min-h-[420px] ${flip ? "lg:order-last" : ""}`}>{w.image ? <img src={w.image} alt={w.title} className="h-full w-full object-cover" /> : <span className="eyebrow">No photo</span>}</div>
              <div className="lg:col-span-8">
                <div className="eyebrow" style={{ color: tone }}>Bottle {String(i + 1).padStart(2, "0")}{w.quantity > 1 ? ` · ×${w.quantity}` : ""}{w.vendor ? ` · ${w.vendor}` : ""}</div>
                <h2 className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">{w.title}</h2>
                {facts.length > 0 && <dl className="mt-4 flex flex-wrap gap-2">{facts.map(([k, v]) => <div key={k} className="border border-border px-3 py-1 text-xs"><dt className="inline uppercase tracking-wider text-neutral-400">{k} </dt><dd className="inline text-neutral-800">{v}</dd></div>)}</dl>}
                {w.tasting_html && <div className="prose-wine mt-5 max-w-2xl text-[15px] leading-relaxed text-neutral-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(w.tasting_html, { USE_PROFILES: { html: true } }) }} data-testid={`feature-note-${w.product_handle}`} />}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
